/**
 * Authentication mutations and session lifecycle.
 *
 * The hooks exist (rather than calling `authApi` from components) so that the *consequences* of an auth change are
 * declared once: which caches are dropped, where the user is sent, and what the shell shows while the app is
 * restoring a session after a reload.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  authApi,
  type OrganisationSessionPayload,
  type RegisterPayload,
} from '@/features/auth/api/authApi';
import { createOrganisationOidcClient } from '@/features/auth/organisation/organisationOidc';
import { queryKeys } from '@/lib/queryKeys';
import { refreshAccessToken } from '@/lib/tokenRefresh';
import { selectIsAuthenticated, useAuthStore } from '@/stores/authStore';
import { useUiStore } from '@/stores/uiStore';
import type { UserProfile } from '@/types/api';

/** Route the guards send an unauthenticated visitor to. */
export const SIGN_IN_ROUTE = '/auth/login';

/** Route a successful sign-in or registration lands on. */
export const POST_AUTH_ROUTE = '/dashboard';

/** Reads the authenticated profile from the store (kept fresh by the auth mutations). */
export function useCurrentUser(): UserProfile | null {
  return useAuthStore((state) => state.user);
}

/** Reports whether a session is present in memory. */
export function useIsAuthenticated(): boolean {
  return useAuthStore(selectIsAuthenticated);
}

/** Mirrors the account profile into the UI store so locale and theme survive without a second request. */
function usePreferencesSync(): void {
  const user = useCurrentUser();
  const setLocale = useUiStore((state) => state.setLocale);
  const setTheme = useUiStore((state) => state.setTheme);

  useEffect(() => {
    if (user) {
      setLocale(user.locale.startsWith('en') ? 'en' : 'fa');
      setTheme(user.theme);
    }
  }, [user, setLocale, setTheme]);
}

/**
 * Restores a session after a page reload.
 *
 * The access token lives in memory only, so a reload starts signed out while the `HttpOnly` refresh cookie is
 * still valid. This hook makes exactly one silent exchange attempt, on mount, and records the outcome in
 * `isBootstrapped` — which is what lets the guards render a skeleton instead of bouncing the user to the sign-in
 * page during that window.
 */
export function useSessionBootstrap(): { isBootstrapped: boolean } {
  const isBootstrapped = useAuthStore((state) => state.isBootstrapped);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      const session = await refreshAccessToken();

      if (!cancelled && !session) {
        useAuthStore.getState().setBootstrapped();
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  usePreferencesSync();

  return { isBootstrapped };
}

/** Exchanges credentials for a session and primes the profile cache. */
export function useLogin() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: authApi.login,
    onSuccess: (session) => {
      useAuthStore.getState().setSession(session);
      queryClient.setQueryData(queryKeys.profile, session.user);
    },
  });
}

/** Creates an account. The answer drives the "verify your e-mail" screen. */

/**
 * Signs out.
 *
 * The local session is dropped in `onSettled`, not `onSuccess`: if the server call fails because the network is
 * gone, the user still asked to leave and the UI must not keep them signed in.
 */
export function useLogout() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (allDevices: boolean) => authApi.logout(allDevices),
    onSettled: () => {
      useAuthStore.getState().clear();
      queryClient.clear();
      navigate(SIGN_IN_ROUTE, { replace: true });
    },
  });
}

/** Signs the user out of every device. */
export function useLogoutEverywhere() {
  return useLogout();
}

/** Starts the reset flow. The UI shows the same confirmation whatever the server decided. */
export function useForgotPassword() {
  return useMutation({
    mutationFn: (email: string) => authApi.forgotPassword(email),
  });
}

/** Completes the reset flow; every session is revoked, so the user must sign in again. */
export function useResetPassword() {
  return useMutation({
    mutationFn: authApi.resetPassword,
  });
}

/**
 * Confirms an e-mail address from the token in the verification link.
 *
 * The token is single-use, so refetching is disabled: a second attempt would consume a token that is already
 * spent and turn a success screen into an error.
 */
export function useVerifyEmail(token: string | null) {
  return useQuery({
    queryKey: ['auth', 'verify-email', token],
    enabled: token !== null,
    retry: false,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    queryFn: () => authApi.verifyEmail(token as string),
  });
}

/** Requests a fresh verification e-mail. */
export function useResendVerification() {
  return useMutation({
    mutationFn: (email: string) => authApi.resendVerification(email),
  });
}

/** Changes the password of the signed-in account. */
export function useChangePassword() {
  return useMutation({
    mutationFn: authApi.changePassword,
  });
}

/**
 * Reads and revokes device sessions.
 *
 * Revoking the *current* session clears the in-memory credentials and every cache, because the API has already
 * deleted the refresh cookie: keeping the store populated would leave the SPA making calls it can no longer
 * refresh.
 */
export function useSessions(enabled: boolean) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: queryKeys.sessions,
    enabled,
    queryFn: authApi.listSessions,
  });

  const revoke = useMutation({
    mutationFn: (familyId: string) => authApi.revokeSession(familyId),
    onSuccess: (result) => {
      if (result.wasCurrentSession) {
        useAuthStore.getState().clear();
        queryClient.clear();
        return;
      }

      void queryClient.invalidateQueries({ queryKey: queryKeys.sessions });
    },
  });

  return { ...query, revoke };
}

export function useRegister() {
  return useMutation({
    mutationFn: (payload: RegisterPayload) => authApi.register(payload),
  });
}

/**
 * Reads the registration and verification policy in force (TASK-0062).
 *
 * `staleTime` of five minutes because the answer only changes when an administrator writes an override, and an override invalidates
 * this key; `retry` is left at the query default so a transient failure does not leave an empty registration form. A screen that
 * cannot read the policy renders its error state rather than guessing a channel.
 */
export function useAuthPolicy() {
  return useQuery({
    queryKey: queryKeys.authPolicy,
    queryFn: authApi.getAuthPolicy,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Redeems a code for the caller's identifier (TASK-0062).
 *
 * The answer's verification object is copied into the cached profile and the profile is re-read: the banner and the route guard branch
 * on that object, so leaving it stale would keep the account looking pending after it had been verified. Nothing else is cached here —
 * the code routes are anonymous, and a caller that was already signed in keeps its session untouched.
 */
export function useVerifyCode() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: authApi.verifyCode,
    onSuccess: () => {
      // The answer reports which channel was settled rather than the new state, so the profile is re-read: the verification object it
      // carries is what the banner and the guard branch on.
      void queryClient.invalidateQueries({ queryKey: queryKeys.profile });
    },
  });
}

/** Requests another verification challenge; the cooldown is answered with `429` and a `Retry-After`. */
export function useResendChallenge() {
  return useMutation({
    mutationFn: authApi.resendVerificationChallenge,
  });
}

/** Registers the mobile number a pending account still has to prove. */
export function useRegisterMobile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: authApi.registerMobile,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.profile });
    },
  });
}

/**
 * Starts an external sign-in by navigating the browser to the API's challenge route (TASK-0062).
 *
 * A hook rather than a mutation because there is no answer to await: the browser leaves the page. Keeping the URL construction next
 * to the other auth calls is what stops a component from hard-coding an API path.
 */
export function useExternalSignIn() {
  return {
    /** Navigates to the provider, optionally starting an explicit link for the signed-in owner. */
    start(provider: string, options: { returnPath?: string; link?: boolean } = {}): void {
      window.location.assign(authApi.externalChallengeUrl(provider, options));
    },
  };
}

/**
 * Exchanges the callback's ticket for a session (TASK-0062).
 *
 * The session is stored exactly as `useLogin` stores it, so the rest of the application cannot tell a provider sign-in from a
 * password sign-in. The ticket is single-use, which is why the callback page calls this once and never retries it blindly.
 */
export function useExchangeExternalTicket() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (ticket: string) => authApi.exchangeExternalTicket(ticket),
    onSuccess: (session) => {
      useAuthStore.getState().setSession(session);
      queryClient.setQueryData(queryKeys.profile, session.user);
    },
  });
}

/** Reads and mutates the provider identities attached to the account (TASK-0062). */
export function useExternalIdentities(enabled: boolean) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: queryKeys.externalIdentities,
    enabled,
    queryFn: authApi.listExternalIdentities,
  });

  const link = useMutation({
    mutationFn: (ticket: string) => authApi.linkExternalIdentity(ticket),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.externalIdentities });
    },
  });

  const unlink = useMutation({
    mutationFn: ({ identityId, reason }: { identityId: string; reason?: string }) =>
      authApi.unlinkExternalIdentity(identityId, reason),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.externalIdentities });
    },
  });

  return { ...query, link, unlink };
}

/**
 * Starts the organisation sign-in (TASK-0064 §3).
 *
 * The SPA is the public OIDC client: the button fetches the deployment's public client configuration, builds the OIDC
 * client from it and hands the browser to the discovered authorization endpoint. The option is offered only when the
 * policy reports the method enabled **and** the provider configured — the same two facts the backend's own routes gate on —
 * and the configuration read is what turns "configured" into a usable flow: a provider the deployment has not finished
 * setting up answers `404` here and renders no button at all.
 */
export function useOrganisationSignIn() {
  const policy = useAuthPolicy();

  const deliverable = (policy.data?.externalProviders ?? []).some(
    (provider) => provider.provider === 'organisation' && provider.deliverable,
  );

  const config = useQuery({
    queryKey: queryKeys.organisationClient,
    queryFn: authApi.getOrganisationClientConfig,
    enabled: deliverable,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  return {
    /** Whether the organisation option may be rendered at all. */
    available: deliverable && config.isSuccess,
    /** Whether the configuration read or the redirect is still in flight. */
    isPending: policy.isLoading || (deliverable && config.isPending),
    /**
     * Redirects the browser to the organisation authority.
     *
     * @param returnPath - SPA path to return to once the flow completes; the OIDC client carries it through the round trip.
     */
    start(returnPath?: string): void {
      if (!config.data) {
        return;
      }

      void createOrganisationOidcClient(config.data).loginAsync(returnPath ?? POST_AUTH_ROUTE);
    },
  };
}

/**
 * Establishes the Prompt Forge session from a completed organisation callback (TASK-0064).
 *
 * The session is stored exactly as `useLogin` stores it, so the rest of the application cannot tell an organisation
 * sign-in from a password sign-in. The IdP tokens are dropped right after: the session is the backend's, and no long-lived
 * provider token has any business staying in the browser.
 */
export function useEstablishOrganisationSession() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: OrganisationSessionPayload) =>
      authApi.establishOrganisationSession(payload),
    onSuccess: (session) => {
      useAuthStore.getState().setSession(session);
      queryClient.setQueryData(queryKeys.profile, session.user);
    },
  });
}
